import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { GlassCard } from "../../components/ui/glass-card";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const getErrorMessage = (err: any) => {
    const detail = err?.response?.data?.detail;
    if (Array.isArray(detail)) {
      return (
        detail
          .map((d: any) => d?.msg || d?.detail || "")
          .filter(Boolean)
          .join(" | ") || "Email o password non validi"
      );
    }
    if (typeof detail === "string") return detail;
    return "Email o password non validi";
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    if (!email.trim() || !password.trim()) {
      toast.error("Inserisci email e password");
      return;
    }

    setIsLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err: any) {
      if (err?.response?.status === 403) {
        toast.error("Email non verificata. Controlla la posta.");
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const primaryColor = theme.palette.primary.main;
  const primaryLightColor = theme.palette.primary.light;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        position: "relative",
        overflow: "auto",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "192px",
          height: "192px",
          borderRadius: "50%",
          backgroundColor: "rgba(0, 161, 156, 0.1)",
          filter: "blur(60px)",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "288px",
          height: "288px",
          borderRadius: "50%",
          backgroundColor: "rgba(146, 214, 213, 0.1)",
          filter: "blur(60px)",
          zIndex: 1,
        }}
      />

      <GlassCard
        hover={false}
        className="w-full p-4 md:p-8 relative z-10 w-[28rem]"
      >
        <div className="flex justify-center mb-6">
          <img src="/assets/long_light.png" alt="logo" className=" rounded-lg" style={{ height: "5rem" }} />
        </div>

        <Typography
          variant="h5"
          component="h1"
          align="center"
          sx={{
            mb: 4,
            fontWeight: 600,
            background: `linear-gradient(135deg, ${primaryColor}, ${primaryLightColor})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Accedi
        </Typography>

        <Box
          component="form"
          onSubmit={handleLogin}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <TextField
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="mario@effia.com"
            fullWidth
            required
            variant="outlined"
            slotProps={{ input: { sx: { borderRadius: "8px" } } }}
          />

          <TextField
            id="password"
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            fullWidth
            required
            variant="outlined"
            slotProps={{ input: { sx: { borderRadius: "8px" } } }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            fullWidth
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              background: `linear-gradient(135deg, ${primaryLightColor}, ${primaryColor})`,
              transition: "filter 0.2s ease-in-out",
              "&:hover": { filter: "brightness(0.9)" },
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Accedi"}
          </Button>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Link
            component="button"
            type="button"
            onClick={() => navigate("/forgot-password")}
            sx={{
              color: primaryLightColor,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Password dimenticata?
          </Link>
        </Box>
      </GlassCard>
    </Box>
  );
};

export default LoginPage;

